import React, { createContext, RefObject, useCallback, useContext, useState, useRef, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import * as SessionStorageWrapper from "../../../../utils/SessionStorageWrapper";
import { PERSONAL_ADDRESS, DHL_PACKSTATION, SELF_PICKUP, PRODUCT_TYPE_PRESCRIPTION_DRUG, PRODUCT_TYPE_FREE_DRUG, PRODUCT_TYPE_TEST_KIT, SHIPPING_COMPANY_UBER, SHIPPING_COMPANY_URBIFY, RX_UPSELL_MARKETS, RX_UPSELL_DECLINED_KEY, SELF_PICKUP_PAYMENT_MODELS } from '../constants';
import { useCartContext } from '../../../../customHooks/useCart';
import { useAppContext } from '../../../../providers/AppProvider';
import { MainTracker } from '../../../../PixelTrackers/MainTracker';
import { translate } from '../../../../utils/translator';
import * as http from "../../../../utils/http";
import { clientConfig } from '../../../../config';
import { ORDER_FLOWS, SERVICE_FEE_TEST_NAME, CANNABIS_ID, FLOWZZ_PREMIUM, SEARCH_PARAM_FOR_SHIPPING_METHOD_UNAVAILABLE } from '../../../../utils/consts';
import { isZeroOrderPrice } from '../../../../utils/helpers';
import { UpsellProduct, CustomerAddress, ExpressTier } from 'data-types';
import { resolveExpressUpsellVariant, doesExpressStockCoverCart, UPSELL_CONFIGS, type ExpressUpsellVariant } from '../components/ExpressDeliveryUpsellPopup';
import { resolveExpressQuoteAvailability } from '../utils/expressQuoteAvailability';
import { resolveSeededTierReconciliation } from '../utils/expressSeededTierReconciliation';
import { resolveRxAutoDefaultTier, resolveCannabisAutoUpgradeTier } from '../utils/expressAutoDefaultTier';
import { shouldRevalidateExpressZip } from '../utils/expressZipRevalidation';
import { useCustomer } from '../../../../customHooks/useCustomer';
import { clearQuestionnaireAnswers, consumePendingClear } from '../../../../utils/QuestionnaireAnswersStorage';
import { getWlExperimentTrackingProps, registerWlExperimentSuperProperties } from '../../../../utils/wlCarePlusExperiment';
import { SHIPPING_EVENTS, buildUpsellPopupProps } from '../utils/shippingTrackingEvents';
import { useCouponWidget, type CouponWidgetState } from '../../../../customHooks/useCouponWidget';
import { markPaymentFromShipping } from '../../../Payment/paymentBackOrigin';


/**
 * Maps a chosen express tier to the provider (shipping_company_code) that fulfills it.
 * Drives the persisted company on the cart, so it must be exact:
 *   uber_2h → Uber, same_day → Urbify, next_day → Urbify.
 * Returns undefined when the tier is unknown/missing (caller treats as "no express company").
 */
const resolveExpressCompanyForTier = (expressTier?: string): string | undefined => {
  switch (expressTier) {
    case 'uber_2h':  return SHIPPING_COMPANY_UBER;
    case 'same_day':
    case 'next_day': return SHIPPING_COMPANY_URBIFY;
    default:         return undefined;
  }
};

type OptionPanelCode = typeof PERSONAL_ADDRESS | typeof DHL_PACKSTATION | typeof SELF_PICKUP;

type DigitalCannabisIDState = {
  showWidget: boolean,
  isToggleEnabled: boolean,
  personalIDNumber: string,
  errorMsg?: string
  productItem: ProductItem | null
  widgetRef: RefObject<HTMLDivElement> | null
  isToggleLoading:boolean
}

type ExpressDeliveryState = {
  isAvailable: boolean;  // Show Express option if the cart has Express products and user is in range
  isEnabled: boolean;     // Enable/disable Express delivery option based on user's location
  isSelected: boolean;      // Express option selected by the user
  deliveryPrice: number;
  deliveryPriceLoading: boolean;
  bonus: {
    type: string;
    value: number;
  } | null;
  shippingMethodToProductIDs: Record<number, number[]>;  // Express shipping method to product IDs
  showSplitDeliveryPopup: boolean;
  showDeliveryInfoPopup: boolean;
  expressPharmacyName: string | null;
  isExpressOnly: boolean;
  isNextDayEligible: boolean;
  nextDayFee: number;
  selectedTier: string | null;
}

type IdentityCheckState = {
  showWidget: boolean,
  isPrecautionToggleEnabled: boolean;
  precautionLevel: string;
}

type ByteplantAddressValidatorState = {
  showModal: boolean
  shouldValidate: boolean,
  responseData: ByteplantApiResponse | null,
  customerUsedSuggestion: boolean
}

type OutOfStockDataState = {
  outOfStockProductItemId: number,
  newProductItemId: number,
  newOTCQuantity: number,
  productType: string,
}

export type PharmacyLocation = {
  name: string,
  address: string;
  location?: { lat: number; lng: number };
  is_external_pharmacy: boolean;
  opening_hours?: string;
  pharmacy_id?: number;
  for_subscription?: boolean;
}

export type DeliveryOptionType = "only_prescription" | "express" | "regular" | "packstation" | "self_pickup";

/*
  the RX Self-Pickup block returned by the shipping GET.

  isEligible false means the customer is outside the rollout, the country is unsupported, or no
  pharmacy can fulfil the exact product — in which case the Self-Pickup row is not rendered at all
  (never as a disabled or empty card).
*/
export type RxSelfPickupState = {
  isEligible: boolean;
  /* Which of the two payment models have at least one pharmacy that can fulfil the product. */
  availablePaymentModels: string[];
  /* Count of eligible pharmacies — not total pharmacy accounts — shown in the search title. */
  eligiblePharmacyCount: number;
  /* Eligible pharmacies keyed by stock code, same shape the cannabis picker consumes. */
  pharmacyCards: { [stockCode: string]: PharmacyLocation };
  /* Pharmacies for the "prescription only" payment model — external pharmacies with a pharmacy_id link. */
  prescriptionOnlyPharmacyCards: { [key: string]: PharmacyLocation };
  fastApprovalPrice: number;
  fastApprovalAvailable: boolean;
  ineligibilityReason?: string;
}

export const RX_SELF_PICKUP_INITIAL_STATE: RxSelfPickupState = {
  isEligible: false,
  availablePaymentModels: [],
  eligiblePharmacyCount: 0,
  pharmacyCards: {},
  prescriptionOnlyPharmacyCards: {},
  fastApprovalPrice: 0,
  fastApprovalAvailable: false,
};


// The catalog auto-discount A/B test arm, rolled client-side and kept in localStorage
// (`abtest_auto_catalog_coupon`). variant null = not a participant (FTB, or a cart with no
// catalog auto-discount).
export type AbtestAutoCatalogCouponState = {
  variant: string | null;
  eligibleForCoupon: boolean;
}

interface ShippingPageContextType {

  // ---- Global states ----
  language: string | undefined;
  country: string | undefined;
  finishedFetching: boolean;
  setFinishedFetching: (finished: boolean) => void
  selectedOptionPanelCode: OptionPanelCode;
  setSelectedOptionPanelCode: (code: OptionPanelCode) => void
  isGoogleAPILoaded: boolean;
  hasUserChosenDeliveryOption: boolean;
  wasExpressPreSelected: boolean;

  saleVoucherCode: string;
  setSaleVoucherCode: (code: string) => void
  saleVoucherRequired: boolean;
  setSaleVoucherRequired: (required: boolean) => void
  isVoucherValid: boolean;
  setIsVoucherValid: (valid: boolean) => void
  setIsGoogleAPILoaded: (loaded: boolean) => void
  selectedFormID: string;
  setSelectedFormID: (formID: string) => void
  countryCurrenciesByID: CountryCurrenciesByID // By countryID
  setCountryCurrenciesByID: (currencies: CountryCurrenciesByID) => void
  customerAddress: Record<"packstation" | "regular", any>; // TODO: maybe type to customer addresses in the client ??
  setCustomerAddress: (address: Record<"packstation" | "regular", any>) => void
  allCustomerAddresses: CustomerAddress[];
  setAllCustomerAddresses: (addresses: CustomerAddress[]) => void;
  invalidPhoneAddress: CustomerAddress | null;
  setInvalidPhoneAddress: (address: CustomerAddress | null) => void;
  preSelectedExternalPharmacy: ExternalPharmacy | null
  setPreSelectedExternalPharmacy: (pharmacy: ExternalPharmacy | null) => void
  selfPickupPharmacySettings: any,
  setSelfPickupPharmacySettings: (settings: any) => void
  hidePackstation: boolean,
  setHidePackstation: (shouldHide: boolean) => void
  contentType: string,
  setContentType: (contentType: string) => void
  testLabSampleType: string | null,
  setTestLabSampleType: (sampleType: string | null) => void
  isMixedOtcTestLabCart: boolean,
  setIsMixedOtcTestLabCart: (isMixed: boolean) => void
  isPackstationFirst: boolean
  isAlternativeOfferTreatment: boolean
  abtestAutoCatalogCoupon: AbtestAutoCatalogCouponState,
  setAbtestAutoCatalogCoupon: (state: AbtestAutoCatalogCouponState) => void
  urbifyRolloutPercentage: number | null,
  setUrbifyRolloutPercentage: (probability: number | null) => void
  isUrbifyFullyRolledOut: boolean
  standardShippingMethodID: number | null,
  setStandardShippingMethodID: (id: number | null) => void
  selfPickupShippingMethodID: number | null,
  setSelfPickupShippingMethodID: (id: number | null) => void
  categoryCatalogMention: string,
  setCategoryCatalogMention: (catalogMention: string) => void
  allowPrescriptionRedemption: boolean,
  setAllowPrescriptionRedemption: (allow: boolean) => void
  isSubscriptionAlreadyPicked: boolean,
  setIsSubscriptionAlreadyPicked: (isPicked: boolean) => void
  OTCAddonsProducts: any[],
  setOTCAddonsProducts: (OTCAddonsProducts: any[]) => void
  showOtcCartPopUp:boolean,
  setShowOtcCartPopUp:(allow: boolean) => void,
  showOTCAddonsModal: boolean,
  setShowOTCAddonsModal: (show: boolean) => void
  couponWidgetState: CouponWidgetState,
  setCouponWidgetState: React.Dispatch<React.SetStateAction<CouponWidgetState>>,
  isDeepLinkCouponApplied: boolean
  setIsDeepLinkCouponApplied: React.Dispatch<React.SetStateAction<boolean>>,
  digitalCannabisIDState: DigitalCannabisIDState,
  setDigitalCannabisIDState: React.Dispatch<React.SetStateAction<DigitalCannabisIDState>>,
  identityCheckState: IdentityCheckState,
  setIdentityCheckState: React.Dispatch<React.SetStateAction<IdentityCheckState>>,
  byteplantAddressValidatorState: ByteplantAddressValidatorState,
  setByteplantAddressValidatorState: React.Dispatch<React.SetStateAction<ByteplantAddressValidatorState>>,
  isSubmitButtonDisabled: boolean,
  setIsSubmitButtonDisabled: (isButtonDisabled: boolean) => void,
  forceFreeTreatmentFeeCoupon: boolean,
  setForceFreeTreatmentFeeCoupon : (couponCode: boolean) => void, 
  getExpressQuote: (formData: Partial<DeliveryAddressFormType>) => Promise<boolean>
  expressDeliveryState: ExpressDeliveryState,
  setExpressDeliveryState: React.Dispatch<React.SetStateAction<ExpressDeliveryState>>
  showUpsellPopUp: boolean,
  setShowUpsellPopUp: (show: boolean) => void
  isFlowzzMoreExpensive: boolean,
  setIsFlowzzMoreExpensive: (isMoreExpensive: boolean) => void
  upsellSaveAmount: number,
  setUpsellSaveAmount: (saveAmount: number) => void
  upsellProducts: UpsellProduct[],
  setUpsellProducts: (products: UpsellProduct[]) => void
  finalAlternativeProducts: Record<number, UpsellProduct>,
  setFinalAlternativeProducts: (products: Record<number, UpsellProduct>) => void
  currentFormData: DeliveryAddressFormType | DHLPackstationFormType | null
  setCurrentFormData: (formData: DeliveryAddressFormType | DHLPackstationFormType | null) => void
  eligibleForUpsell: boolean,
  setEligibleForUpsell: (eligible: boolean) => void
  wasUpgradedToPremium: boolean,
  setWasUpgradedToPremium: (wasUpgraded: boolean) => void,
  showUpsellOnShippingPage: boolean, // if the original flow of cart is flowz or all the products are prescription only, ahow the popup on shipping page loading and not on the shipping page submit 
  setshowUpsellOnShippingPage: (show: boolean) => void
  showRescheduleVideoConsultationModal: boolean,
  setShowRescheduleVideoConsultationModal: (show: boolean) => void
  isOriginalCartFlowFlowzz: boolean,
  setIsOriginalCartFlowFlowzz: (isOriginal: boolean) => void
  upsellABCtestWeights: { A: number; B: number } | null,
  setUpsellABCtestWeights: (weights: { A: number; B: number } | null) => void
  outOfStockDataState: {outOfStockData: OutOfStockDataState[], originalProducts: CartProductView[]},
  setOutOfStockDataState: React.Dispatch<React.SetStateAction<{outOfStockData: OutOfStockDataState[], originalProducts: CartProductView[]}>>
  setExpressStockCode: (stockCode: string | null) => void
  updateCartZipcode: (zipcode: string) => void
  expressStockCode: string | null
  otcOnlyShippingFee: number,
  setOtcOnlyShippingFee: (fee: number) => void
  setUpsellPopupDismissed: (shown: boolean) => void
  upsellPopupDismissed: boolean
  showRxPremiumUpsellPopup: boolean
  setShowRxPremiumUpsellPopup: (show: boolean) => void
  rxWasUpgradedToPremium: boolean
  showRxRegretBanner: boolean
  handleRxUpsellAccept: () => Promise<void>
  handleRxUpsellDecline: () => void
  handleRxUpsellClose: () => void
  handleRxRegretDismiss: () => void
  // ---- Global handlers (General functions) ----
  handleShippingSubmit: (formData: DeliveryAddressFormType | DHLPackstationFormType, customerUsedAddressSuggestion?: boolean, isExpress?: boolean, skipUpsell?: boolean, wasUpgradedToPremiumParam?: boolean) => void
  handleDefaultCartCurrency: (currencies: CountryCurrenciesByID) => void
  handleApplyCoupon: ({inputCode, shouldOverrideCartBonuses, isExpressSelected}: {inputCode: string, shouldOverrideCartBonuses?: boolean, isExpressSelected?: boolean}) => void
  handleDigitalCannabisToggle: (newIsToggleOn: boolean) => void
  handleAddOTCProducts:(productItems: ProductItem[]) => Promise<void>
  handleOTCProductIncrement: (productItem: ProductItem, source?: string) => Promise<void>
  handleOTCProductDecrement: (productItem: ProductItem) => Promise<void>
  handleDeliveryOptionChange: (type: DeliveryOptionType, expressTier?: string, options?: { isManualSelection?: boolean; selfPickupPharmacyID?: number; selfPickupPaymentModel?: string; isExpressPrescriptionReview?: boolean }) => Promise<void>
  // RX Self-Pickup
  isReturningCustomer: boolean
  setIsReturningCustomer: (v: boolean) => void
  rxSelfPickup: RxSelfPickupState
  setRxSelfPickup: React.Dispatch<React.SetStateAction<RxSelfPickupState>>
  selectedPickupPharmacyCode: string | null
  setSelectedPickupPharmacyCode: (stockCode: string | null) => void
  rxSelfPickupServiceOption: string
  setRxSelfPickupServiceOption: (serviceOption: string) => void
  isPickupSheetOpen: boolean
  setIsPickupSheetOpen: (isOpen: boolean) => void
  pharmacyRequiredError: boolean
  setPharmacyRequiredError: (v: boolean) => void
  isRxPickupFastApproval: boolean
  setIsRxPickupFastApproval: (v: boolean) => void
  handleAddressValidation: (formData: DeliveryAddressFormType) => void
  upgradePrescriptionOnlyToPremium: (productIdsToRemove: number[], productItemsToAdd: number[]) => Promise<void>,  setUriForOutOfStock: (uri: string) => void
  pharmaciesModalInfo?: { title?: string; subtitle?: string; pharmacies?: { name: string; address: string; email?: string; phone?: string }[] } | null,
  setPharmaciesModalInfo?: React.Dispatch<React.SetStateAction<{ title?: string; subtitle?: string; pharmacies?: { name: string; address: string; email?: string; phone?: string }[] } | null>>
  showFastReviewUpsell: boolean,
  setShowFastReviewUpsell: (show: boolean) => void
  isFastReviewUpsellShown: React.MutableRefObject<boolean>
  allPharmaciesLocations: { [key: string]: PharmacyLocation }
  setAllPharmaciesLocations: (locations: { [key: string]: PharmacyLocation }) => void
  isExpressPrescriptionReview: boolean,
  setIsExpressPrescriptionReview: (isExpressPrescriptionReview: boolean) => void,
  productsGroupedByPharmacy: { pharmacyName: string, productItemIDs: number[] }[],
  setProductsGroupedByPharmacy: (productsGroupedByPharmacy: { pharmacyName: string, productItemIDs: number[] }[]) => void
  isCannabisCatalog: boolean,
  setIsCannabisCatalog: (isCannabisCatalog: boolean) => void
  isExpressFlow: boolean,
  setIsExpressFlow: (isExpressFlow: boolean) => void
  isCarePlusOrder: boolean,
  setIsCarePlusOrder: (isCarePlusOrder: boolean) => void
  isFastApprovalFlow: boolean,
  setIsFastApprovalFlow: (isFastApprovalFlow: boolean) => void
  showExpressDeliveryUpsell: boolean
  setShowExpressDeliveryUpsell: (show: boolean) => void
  expressUpsellVariant: ExpressUpsellVariant | null
  expressDeliveryUpsellShown: React.MutableRefObject<boolean>
  availableExpressTiers: ExpressTier[]
  setAvailableExpressTiers: React.Dispatch<React.SetStateAction<ExpressTier[]>>
  handleExpressUpsellApply: () => void
  handleExpressUpsellContinue: () => void
  handleExpressUpsellClose: () => void
}

// Create the context
const ShippingPageContext = createContext<ShippingPageContextType | undefined>(undefined);

// Packstation panel above the home-address panel: ED (HL-10299) and DE test-lab-only carts (HL-12255).
export const isPackstationFirstLayout = (
  catParam: string | null,
  country: string | undefined,
  cartProducts: Array<{ is_test_lab?: boolean }>,
): boolean => {
  if (catParam === 'erectile_dysfunction') return true;
  const isTestLabOnlyCart: boolean = cartProducts.length > 0 && cartProducts.every((product) => product.is_test_lab);
  return country === 'de' && isTestLabOnlyCart;
};

// Create the provider
export const ShippingPageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

  const { setIsLoading, setStickyNotification } = useAppContext();
  const {  getCart, clearCart, setCartCurrency, saveCart, deactivateSubscriptionInDB, addProductItemsToCart,incrementProductItemQuantity, decrementProductItemQuantity } = useCartContext();
  const { customerState } = useCustomer();
  let cart = getCart();
  const [searchParams] = useSearchParams();
  const [expressStockCode, setExpressStockCode] = useState<string | null>(null);
  const navigate = useNavigate();
  const { language, country } = useParams<{ language: string, country: string }>();
  const [finishedFetching, setFinishedFetching] = useState(false);
  const [selectedOptionPanelCode, setSelectedOptionPanelCode] = useState<OptionPanelCode>(PERSONAL_ADDRESS);
  const [isGoogleAPILoaded, setIsGoogleAPILoaded] = useState(false);
  const [hasUserChosenDeliveryOption, setHasUserChosenDeliveryOption] = useState<boolean>(() => {
    if (country === "de") {
      return !!getCart().selected_service_option;
    }
    return false;
  });
  const [selectedFormID, setSelectedFormID] = useState("");
  const [saleVoucherCode, setSaleVoucherCode] = useState<string>("");
  const [saleVoucherRequired, setSaleVoucherRequired] = useState<boolean>(false);
  const [isVoucherValid, setIsVoucherValid] = useState<boolean>(false);
  const [countryCurrenciesByID, setCountryCurrenciesByID] = useState<CountryCurrenciesByID>({});
  const [customerAddress, setCustomerAddress] = useState<Record<"packstation" | "regular", any>>({"packstation": {}, "regular": {}});
  const [allCustomerAddresses, setAllCustomerAddresses] = useState<CustomerAddress[]>([]);
  const [invalidPhoneAddress, setInvalidPhoneAddress] = useState<CustomerAddress | null>(null);
  const [preSelectedExternalPharmacy, setPreSelectedExternalPharmacy] = useState<ExternalPharmacy | null>(null);
  const [selfPickupPharmacySettings, setSelfPickupPharmacySettings] = useState<any>(null);
  const [hidePackstation, setHidePackstation] = useState(false);
  const [contentType, setContentType] = useState<string>("");
  const [testLabSampleType, setTestLabSampleType] = useState<string | null>(null);
  const [isMixedOtcTestLabCart, setIsMixedOtcTestLabCart] = useState(false);
  const [categoryCatalogMention, setCategoryCatalogMention] = useState<string>("");
  const [allowPrescriptionRedemption, setAllowPrescriptionRedemption] = useState(false);
  const [isSubscriptionAlreadyPicked, setIsSubscriptionAlreadyPicked] = useState(false);
  const [OTCAddonsProducts, setOTCAddonsProducts] = useState<any[]>([]);
  const [showOTCAddonsModal, setShowOTCAddonsModal] = useState(false);
  const [showOtcCartPopUp, setShowOtcCartPopUp] = useState(false);
  const [isDeepLinkCouponApplied, setIsDeepLinkCouponApplied] = useState(false);
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(false);
  const [forceFreeTreatmentFeeCoupon, setForceFreeTreatmentFeeCoupon] = useState<boolean>(false);
  const [showUpsellPopUp, setShowUpsellPopUp] = useState<boolean>(false);
  const [showRescheduleVideoConsultationModal, setShowRescheduleVideoConsultationModal] = useState(false);
  const [isFlowzzMoreExpensive, setIsFlowzzMoreExpensive] = useState<boolean>(false);
  const [upsellSaveAmount, setUpsellSaveAmount] = useState<number>(0);
  const [upsellProducts, setUpsellProducts] = useState<UpsellProduct[]>([]);
  const [eligibleForUpsell, setEligibleForUpsell] = useState<boolean>(false);
  const [finalAlternativeProducts, setFinalAlternativeProducts] = useState<Record<number, UpsellProduct>>({});
  const [currentFormData, setCurrentFormData] = useState<DeliveryAddressFormType | DHLPackstationFormType | null>(null);
  const [wasUpgradedToPremium, setWasUpgradedToPremium] = useState<boolean>(false);
  const [showUpsellOnShippingPage, setshowUpsellOnShippingPage] = useState<boolean>(false);
  const [upsellABCtestWeights, setUpsellABCtestWeights] = useState<{ A: number; B: number } | null>(null);
  const [isOriginalCartFlowFlowzz, setIsOriginalCartFlowFlowzz] = useState<boolean>(false);
  const [upsellPopupDismissed, setUpsellPopupDismissed] = useState<boolean>(false)
  const [showRxPremiumUpsellPopup, setShowRxPremiumUpsellPopup] = useState<boolean>(false)
  const [showRxRegretBanner, setShowRxRegretBanner] = useState<boolean>(false)
  const [rxUpsellDismissed, setRxUpsellDismissed] = useState<boolean>(false)
  const [rxWasUpgradedToPremium, setRxWasUpgradedToPremium] = useState<boolean>(false)
  const [uriForOutOfStock,setUriForOutOfStock] = useState("")
  const [outOfStockDataState, setOutOfStockDataState] = useState<{outOfStockData: OutOfStockDataState[], originalProducts: CartProductView[]}>({outOfStockData: [], originalProducts: []});
  const [otcOnlyShippingFee, setOtcOnlyShippingFee] = useState<number>(0);
  const [showFastReviewUpsell, setShowFastReviewUpsell] = useState<boolean>(false);
  const isFastReviewUpsellShown = useRef<boolean>(false);
  const upsaleOfferShownTracked = useRef<boolean>(false);
  const [isFastApprovalFlow, setIsFastApprovalFlow] = useState<boolean>(false);
  const [isExpressPrescriptionReview, setIsExpressPrescriptionReview] = useState<boolean>(false);
  const [productsGroupedByPharmacy, setProductsGroupedByPharmacy] = useState<{ pharmacyName: string, productItemIDs: number[] }[]>([]);
  const [isCannabisCatalog, setIsCannabisCatalog] = useState<boolean>(false);
  const [isExpressFlow, setIsExpressFlow] = useState<boolean>(false);
  // Care+ orders (catalog is_care_plus, from the shipping API). The plan choice on Choose Plan
  // decides prescription-only vs delivered, so Shipping must not switch between them.
  const [isCarePlusOrder, setIsCarePlusOrder] = useState<boolean>(false);
  const [showExpressDeliveryUpsell, setShowExpressDeliveryUpsell] = useState<boolean>(false);
  const [standardShippingMethodID, setStandardShippingMethodID] = useState<number | null>(null);
  const [selfPickupShippingMethodID, setSelfPickupShippingMethodID] = useState<number | null>(null);
  const [expressUpsellVariant, setExpressUpsellVariant] = useState<ExpressUpsellVariant | null>(null);
  const expressDeliveryUpsellShown = useRef<boolean>(false);
  const [availableExpressTiers, setAvailableExpressTiers] = useState<ExpressTier[]>([]);
  const latestExpressTiers = useRef<ExpressTier[]>([]);
  // product_item_ids the quoted express stock can ship — read synchronously at the upsell
  // gate (state lags), mirrors latestExpressTiers. Empty when no express stock / out of range.
  const latestExpressCoverage = useRef<number[]>([]);
  const wasExpressPreSelected = useRef(cart.selected_service_option === 'express').current;
  // The catalog auto-discount A/B test arm, resolved from localStorage on the shipping page.
  const [abtestAutoCatalogCoupon, setAbtestAutoCatalogCoupon] = useState<AbtestAutoCatalogCouponState>({ variant: null, eligibleForCoupon: true });
  const [urbifyRolloutPercentage, setUrbifyRolloutPercentage] = useState<number | null>(null);
  // RX Self-Pickup: eligibility comes from the shipping GET; the selection lives here so it
  // survives switching between delivery options and is revalidated before the customer continues.
  const [isReturningCustomer, setIsReturningCustomer] = useState(false);
  const [rxSelfPickup, setRxSelfPickup] = useState<RxSelfPickupState>(RX_SELF_PICKUP_INITIAL_STATE);
  const [selectedPickupPharmacyCode, setSelectedPickupPharmacyCode] = useState<string | null>(null);
  const [rxSelfPickupServiceOption, setRxSelfPickupServiceOption] = useState<string>(SELF_PICKUP_PAYMENT_MODELS.PRESCRIPTION_ONLY);
  const [isPickupSheetOpen, setIsPickupSheetOpen] = useState(false);
  const [isRxPickupFastApproval, setIsRxPickupFastApproval] = useState(false);
  const [pharmacyRequiredError, setPharmacyRequiredError] = useState(false);

  // Clear the pharmacy-required error as soon as a pharmacy is chosen
  useEffect(() => {
    if (selectedPickupPharmacyCode) {
      setPharmacyRequiredError(false);
    }
  }, [selectedPickupPharmacyCode]);

  // When subscription turns on/off, update rxSelfPickup eligibility client-side:
  // - If subscription is on and no internal pharmacies support it, hide the whole option.
  // - If subscription is turned off again, restore eligibility from the original loaded state.
  // - Also clear any selected pharmacy whose stock doesn't support subscription renewals.
  const cartSubscriptionPlanId = cart.subscription_plan_id;
  const rxSelfPickupBaseRef = useRef<RxSelfPickupState | null>(null);
  useEffect(() => {
    // Snapshot fresh eligible data as the base whenever subscription is off.
    // While subscription is on we skip this — the filtered state must not overwrite
    // the original data we need to restore on toggle-off.
    if (rxSelfPickup.isEligible && !cartSubscriptionPlanId) {
      rxSelfPickupBaseRef.current = rxSelfPickup;
    }

    const base = rxSelfPickupBaseRef.current;
    if (!base) return;

    if (cartSubscriptionPlanId) {
      const hasSubscriptionPharmacies = Object.values(base.pharmacyCards).some((c) => c.for_subscription);
      if (!hasSubscriptionPharmacies) {
        setRxSelfPickup({ ...RX_SELF_PICKUP_INITIAL_STATE, ineligibilityReason: "no_eligible_pharmacy_for_subscription" });
        setSelectedPickupPharmacyCode(null);
        return;
      }
      // There are subscription-capable pharmacies — clear selected if the current pick doesn't qualify.
      if (selectedPickupPharmacyCode) {
        const allCards = { ...base.pharmacyCards, ...base.prescriptionOnlyPharmacyCards };
        const card = allCards[selectedPickupPharmacyCode];
        if (card && !card.for_subscription) {
          setSelectedPickupPharmacyCode(null);
        }
      }
    } else if (rxSelfPickup !== base) {
      // Subscription turned off — restore original eligibility.
      setRxSelfPickup(base);
    }
  }, [rxSelfPickup, cartSubscriptionPlanId]);

  // Once Urbify is at 100% rollout (HL-10487), RX ignores the abtest_urbify localStorage gate
  // entirely (it can't be trusted post-rollout) and always shows/defaults to Urbify when eligible.
  const isUrbifyFullyRolledOut = urbifyRolloutPercentage !== null && Number(urbifyRolloutPercentage) >= 1;
  // Set only when the customer explicitly clicks a shipping option on this page (HL-10487), so
  // the auto-default/auto-upgrade logic in getExpressQuote stops re-picking a tier for them
  // while still re-evaluating on every ZIP/address change beforehand.
  // Arriving because the order's shipping method closed — the customer picks; no auto-default.
  const userManuallySelectedShipping = useRef<boolean>(searchParams.get("ec") === SEARCH_PARAM_FOR_SHIPPING_METHOD_UNAVAILABLE);

  const catParam = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('cat')
    : null;
  const isPackstationFirst = isPackstationFirstLayout(catParam, country, cart.products);

  // Flowzz Premium alternative-offer A/B test as Choose Treatment (HL-10739)
  const isAlternativeOfferTreatment = typeof window !== 'undefined'
    && localStorage.getItem('abtest_offer_alternative_cannabis_choose_treatment') === 'Treatment';

  const [identityCheckState, setIdentityCheckState] = useState<IdentityCheckState>({
    showWidget: false,
    isPrecautionToggleEnabled: false,
    precautionLevel: "info",
  })
  const [pharmaciesModalInfo, setPharmaciesModalInfo] = useState<{ title?: string; subtitle?: string; pharmacies?: { name: string; address: string; email?: string; phone?: string }[] } | null>(null);
  const [allPharmaciesLocations, setAllPharmaciesLocations] = useState<{ [key: string]: { name: string, address: string; location?: { lat: number; lng: number }; is_external_pharmacy: boolean; opening_hours?: string; } }>({});

  const [digitalCannabisIDState, setDigitalCannabisIDState] = useState<DigitalCannabisIDState>({
    showWidget: false,
    isToggleEnabled: false,
    personalIDNumber: "",
    errorMsg: "",
    productItem: null,
    widgetRef: null,
    isToggleLoading: false,
  })

  const [byteplantAddressValidatorState, setByteplantAddressValidatorState] = useState<ByteplantAddressValidatorState>({
    showModal: false,
    shouldValidate: false,
    responseData: null,
    customerUsedSuggestion: false
  })

  // Seed selection display from the cart the page was loaded with — the express choice is
  // already forwarded (selected_service_option / selected_express_tier), so no effect is
  // needed to "restore" it. Selection then changes via handleDeliveryOptionChange, either from
  // a customer click or from the auto-default/auto-upgrade logic in getExpressQuote below.
  const [expressDeliveryState, setExpressDeliveryState] = useState<ExpressDeliveryState>(() => {
    const isExpress = cart.selected_service_option === 'express';
    return {
      isAvailable: false,
      isEnabled: false,
      isSelected: isExpress,
      deliveryPrice: 1,
      deliveryPriceLoading: false,
      bonus: null,
      shippingMethodToProductIDs: {},
      showSplitDeliveryPopup: false,
      showDeliveryInfoPopup: false,
      expressPharmacyName: null,
      isExpressOnly: false,
      isNextDayEligible: false,
      nextDayFee: 0,
      selectedTier: isExpress ? (cart.selected_express_tier ?? null) : null,
    };
  });
  const initialExpressCheckDone = useRef(false);

  // Once the order POST is in flight the cart is about to be soft-deleted server-side
  // (OrdersController -> CartModelV2.softDelete). Block every deferred cart-mutating op
  // (express quote / coupon / delivery-option / zip update) so a late effect or in-flight
  // promise doesn't hit a cart that no longer exists. This is the root cause of the
  // post-order CartControllerV2/activateCoupon "no cartID was found in db" errors (HL-9096).
  const isOrderSubmittingRef = useRef(false);
  const { couponWidgetState, setCouponWidgetState, handleApplyCoupon } = useCouponWidget({
    language,
    isExpressSelected: expressDeliveryState.isSelected,
    isOrderSubmitting: () => isOrderSubmittingRef.current,
    setSubmitButtonDisabled: setIsSubmitButtonDisabled,
    onCartView: (cartView) => setExpressStockCode(cartView.expressStockCode),
  });

  // Load-time (and re-quote-time) auto-selection: RX applies strict Urbify > Uber > DHL
  // priority and cannabis applies a DHL -> Urbify upgrade when it's the same price or
  // cheaper (HL-10487) — both inside getExpressQuote below, both skipped once the customer
  // has explicitly clicked an option on this page (userManuallySelectedShipping).

  //Check if express in range
  const getExpressQuote = async (formData: Partial<DeliveryAddressFormType>) => {

    // Don't quote once the order is being placed — the cart is about to be (or already)
    // soft-deleted, and this call would also fan out into handleApplyCoupon /
    // handleDeliveryOptionChange against a missing cart (HL-9096).
    if (isOrderSubmittingRef.current) return false;

    const shippingDetails = { dropoffAddress: { street: formData.address, zip: formData.zip, city: formData.city, country: formData.country }, zipcode: formData.zip, dropoffPhoneNumber: formData.phone, expressStockCode, isExpressSelected: expressDeliveryState.isSelected };
    return http.postToServer(`express/quote/${categoryCatalogMention}/${country}/${language}`, shippingDetails, [http.MIDDLEWARES.CART_ORDER])
      .then(async (response) => {

        const quoteData = response.data;
        // Update the cartView
        const cartView = response.data.cartView as CartView;
        if (cartView ) {
          // Always save cart to ensure custom_shipping_fee is updated in context
          saveCart({ ...cartView.cart, products: cartView.products, timekitBooking: cartView.timekitBooking });
          setExpressStockCode(cartView.expressStockCode);
        }


        // getCart() is read fresh (saveCart ran earlier in this quote).
        const freshCart = getCart();
        const isSelfPickup = freshCart.selected_service_option === 'self_pickup';

        if (isSelfPickup) return true;

          // try to reactivate coupon
        const couponCode = cartView?.cart?.bonus_coupon_code || couponWidgetState?.inputCode || searchParams?.get('cpn');
        if (couponCode && !cartView?.cart?.subscription_plan_id && !cartView?.cart?.auto_bonus) {
          await handleApplyCoupon({inputCode: couponCode, isExpressSelected: expressDeliveryState.isSelected});
        }
        // Trust the backend's inRange verdict; don't gate on the local expressStockCode, which
        // is null in the next-day-eligible-but-unpinned window and produced a false
        // "express not available" notification (HL-9096). See expressQuoteAvailability.ts.
        const { isAvailable: isExpressInRange, stockCode: expressQuoteStockCode } =
          resolveExpressQuoteAvailability(quoteData, cartView?.expressStockCode, expressStockCode);
        if (isExpressInRange) {
          setExpressDeliveryState((prevState) => {
            const selectedTierFee = prevState.selectedTier && quoteData.allQuotes
              ? quoteData.allQuotes.find((q: any) => q.tier === prevState.selectedTier)?.fee
              : undefined;
            return {
              ...prevState,
              isAvailable: true,
              isEnabled: true,
              deliveryPrice: selectedTierFee ?? quoteData.fee,
              deliveryPriceLoading: false,
            };
          });
          initialExpressCheckDone.current = true;

          if (expressQuoteStockCode) {
            const expressProductItemIDs = cart.products.filter(p => p.available_stocks?.includes(expressQuoteStockCode)).map(p => p.product_item_id)
            latestExpressCoverage.current = expressProductItemIDs;
            setExpressDeliveryState((prevState) => ({
              ...prevState,
              shippingMethodToProductIDs: { [Object.keys(prevState.shippingMethodToProductIDs)[0]]: expressProductItemIDs || [] },
            }));
          } else {
            latestExpressCoverage.current = [];
          }


          if (quoteData.allQuotes) {
            const tiers = quoteData.allQuotes
              .filter((q: any) => q.inRange)
              .map((q: any) => ({
                tier: q.tier,
                isAvailable: q.isCutoffPassed !== true,
                fee: q.fee,
                cutoffTime: q.cutoffTime,
                isCutoffPassed: q.isCutoffPassed,
              }));
            latestExpressTiers.current = tiers;
            setAvailableExpressTiers(tiers);

            // getCart() is read fresh (saveCart ran earlier in this quote).
            const freshCart = getCart();
            const isAlreadyExpress = freshCart.selected_service_option === 'express';
            // Also lock when cart is PO but selected_service_option is still null (Flowzz/Canaleo
            // CT often leaves it unset). Without this, cannabis auto-upgrade picks express/regular
            // and clears only_prescription on shipping load.
            const isPickupOrPrescription =
              freshCart.selected_service_option === SELF_PICKUP ||
              freshCart.selected_service_option === 'only_prescription' ||
              Boolean(freshCart.only_prescription);

            if (contentType === "cannabis") {
              // Reorder/express-preselect fidelity: the cart was seeded with the original order's
              // express tier. If that tier is no longer available (e.g. same_day after cutoff), fall
              // the cart forward to the best available tier of the same provider (next_day), then
              // cross-fade to the other express provider before dropping to regular.
              const seededTierResult = resolveSeededTierReconciliation({
                wasExpressPreSelected: isAlreadyExpress,
                seededTier: freshCart.selected_express_tier,
                company: freshCart.shipping_company_code,
                availableTiers: tiers,
                allowProviderFallback: true,
              });
              if (seededTierResult.action === "select") {
                await handleDeliveryOptionChange("express", seededTierResult.tier);
              } else if (seededTierResult.action === "regular") {
                await handleDeliveryOptionChange("regular");
              } else if (!isAlreadyExpress && !userManuallySelectedShipping.current && !isPickupOrPrescription) {
                // DHL -> Urbify upgrade (HL-10487): a customer who chose DHL at choose-treatment
                // moves to same-day (or next-day) Urbify once it's the same price or cheaper.
                const upgradeTier = resolveCannabisAutoUpgradeTier(tiers, freshCart.regular_shipping_fee ?? 0);
                if (upgradeTier) {
                  await handleDeliveryOptionChange("express", upgradeTier);
                }
              }
            } else if (!userManuallySelectedShipping.current && !(isCarePlusOrder && isPickupOrPrescription)) {
              if (isAlreadyExpress) {
                // Reorder fidelity (HL-10487): the cart was seeded with the original order's
                // express tier. Preserve it if still available, fall forward within the same
                // provider, then cross-fade to the other express provider (urbify <-> uber)
                // before falling back to DHL.
                const seededTierResult = resolveSeededTierReconciliation({
                  wasExpressPreSelected: isAlreadyExpress,
                  seededTier: freshCart.selected_express_tier,
                  company: freshCart.shipping_company_code,
                  availableTiers: tiers,
                  allowProviderFallback: true,
                });
                if (seededTierResult.action === "select") {
                  await handleDeliveryOptionChange("express", seededTierResult.tier);
                } else if (seededTierResult.action === "regular") {
                  await handleDeliveryOptionChange("regular");
                }
              } else {
                // RX strict priority (HL-10487): Urbify > Uber > DHL, re-evaluated on every
                // quote (e.g. a ZIP change) for carts that weren't pre-seeded as express.
                // Only when the panel can actually offer express (isExpressFlow): a returning
                // customer's saved ZIP makes handleGet report next-day eligibility even for a
                // product whose shipping methods exclude express, so the quote still returns
                // tiers. Auto-picking one then sets the cart to express with no express tile
                // rendered — DHL shows unselected while the breakdown bills the express fee.
                const defaultTier = isExpressFlow
                  ? resolveRxAutoDefaultTier(tiers, isUrbifyFullyRolledOut)
                  : null;
                if (defaultTier) {
                  await handleDeliveryOptionChange("express", defaultTier);
                }
              }
            }

            const nextDayTier = tiers.find((t: ExpressTier) => t.tier === 'next_day' && t.isAvailable);
            setExpressDeliveryState((prevState) => ({
              ...prevState,
              isNextDayEligible: !!nextDayTier,
              nextDayFee: nextDayTier?.fee || 0,
            }));
          }

          return true;
        } else {
          initialExpressCheckDone.current = true;
          if (quoteData.uberZipEligibleAddressIneligible) {
            window.clientLogger.warn("Uber zip eligible but address ineligible", { code: "uber_zip_eligible_address_ineligible", shippingDetails });
          }
          if (expressDeliveryState.isSelected) {
            MainTracker.track('custom', 'Changed ZIP Uber');
            setStickyNotification({
              show: true,
              level: "warning",
              showInfoIcon: true,
              title: translate(language, "default", "StickyNotification/express_delivery_not_available_title") as string,
              content: translate(language, "default", "StickyNotification/express_delivery_not_available_description") as string,
              autoFade: false,
            });
          }
          setExpressDeliveryState((prevState) => ({
            ...prevState,
            isEnabled: false,
            deliveryPriceLoading: false,
            isNextDayEligible: false,
            nextDayFee: 0,
          }));
          latestExpressTiers.current = [];
          latestExpressCoverage.current = [];
          setAvailableExpressTiers([]);
          if (expressDeliveryState.isSelected || cart.selected_service_option  == "express") {
            await handleDeliveryOptionChange("regular");
            window.clientLogger.warn("Express delivery not available, switching to regular", { code: "express_delivery_not_available", shippingDetails });
          }

          return false;
        }
      })
      .catch(async () => {
        if (expressDeliveryState.isSelected || cart.selected_service_option  == "express") {
          await handleDeliveryOptionChange("regular");
          window.clientLogger.warn("Express delivery not available, switching to regular", { code: "express_delivery_not_available", shippingDetails });
          setStickyNotification({
            show: true,
            level: "warning",
            showInfoIcon: true,
            title: translate(language, "default", "StickyNotification/express_delivery_unavailable_title") as string,
            content: translate(language, "default", "StickyNotification/express_delivery_unavailable_description") as string,
            autoFade: false,
            });
        }
        setExpressDeliveryState((prevState) => ({
          ...prevState,
          isEnabled: false,
          isSelected: false,
          deliveryPriceLoading: false,
          isNextDayEligible: false,
          nextDayFee: 0,
        }));
        latestExpressTiers.current = [];
        latestExpressCoverage.current = [];
        setAvailableExpressTiers([]);
        return false;
      })
      .finally(() => {
        setExpressDeliveryState((prevState) => ({
          ...prevState,
          deliveryPriceLoading: false,
        }));
      });
  };

  const updateCartZipcode = async (zipcode: string) => {
    // Cart is about to be / already soft-deleted once the order is being placed (HL-9096).
    if (isOrderSubmittingRef.current) return;
    http.postToServer(`cart/update`, { zipcode }, [http.MIDDLEWARES.CART_ORDER, http.MIDDLEWARES.CUSTOMER_ID])
      .then((response) => {
        const cartView = response.data.cartView as CartView;
        if (cartView) {
          saveCart({ ...cartView.cart, products: cartView.products, timekitBooking: cartView.timekitBooking });
          setExpressDeliveryState((prevState) => ({
            ...prevState,
            deliveryPriceLoading: true,
          }));
          setExpressStockCode(cartView.expressStockCode);
        }
      })
      .catch((error) => {
        window.clientLogger.error("Error updating cart zipcode:", error);
      });
  }

  const handleDeliveryOptionChange = async (type: DeliveryOptionType, expressTier?: string, options?: { isManualSelection?: boolean; isRxUpsell?: boolean; selfPickupPharmacyID?: number; selfPickupPaymentModel?: string; isExpressPrescriptionReview?: boolean }) => {
    // Cart is about to be / already soft-deleted once the order is being placed (HL-9096).
    if (isOrderSubmittingRef.current) return;
    // Stop the auto-default/auto-upgrade logic (getExpressQuote) from overriding a choice the
    // customer explicitly made on this page (HL-10487) — including re-clicking the already
    // selected option, so record it before the same-selection short-circuit below.
    if (options?.isManualSelection) {
      userManuallySelectedShipping.current = true;
    }
    // 0. If the current delivery option is the same as the new one, don't do anything.
    // Self-pickup is excluded: the user may reopen the sheet to change the pharmacy or
    // payment model without changing the option type.
    const currentCart = getCart();
    if (currentCart.selected_service_option === type && type !== "self_pickup") {
      if (type !== "express" || expressDeliveryState.selectedTier === expressTier) {
        // The express->regular fallback can find the cart already `regular` and bail out here,
        // skipping the state reset in the response handler below and leaving isSelected stuck
        // on true while the UI shows DHL. Clear it here too.
        if (type !== "express") {
          setExpressDeliveryState((prevState) => (
            prevState.isSelected || prevState.selectedTier || Object.keys(prevState.shippingMethodToProductIDs).length
              ? { ...prevState, isSelected: false, selectedTier: null, shippingMethodToProductIDs: {} }
              : prevState
          ));
        }
        return;
      }
    }

    // 1. Reset the byteplant address validator state
    setByteplantAddressValidatorState({
      showModal: false,
      shouldValidate: false,
      responseData: null,
      customerUsedSuggestion: false
    })

    // 2. Mark that user has actively chosen a delivery option
    setHasUserChosenDeliveryOption(true);

    // 3. Set loading state before updating DB
    setIsLoading(true);

    // 4. Update cart in Redis - send the type directly
    const expressShippingCompany = type === "express"
      ? resolveExpressCompanyForTier(expressTier)
      : undefined;
    return http.postToServer("cart/update-delivery-option", { deliveryOptionType: type, expressShippingCompany, expressTier: type === "express" ? expressTier : undefined, isRxUpsell: options?.isRxUpsell ?? false, ...(type === "self_pickup" && options?.selfPickupPharmacyID ? { selfPickupPharmacyID: options.selfPickupPharmacyID } : {}), ...(type === "self_pickup" && options?.selfPickupPaymentModel ? { selfPickupPaymentModel: options.selfPickupPaymentModel } : {}), ...(type === "self_pickup" && options?.isExpressPrescriptionReview !== undefined ? { isExpressPrescriptionReview: options.isExpressPrescriptionReview } : {}) }, [http.MIDDLEWARES.CART_ORDER, http.MIDDLEWARES.CUSTOMER_ID])
    .then(async (response) => {

      const cartView = response.data.cartView as CartView;
      const externalPharmacyID = response.data.external_pharmacy_id;
      
      // Update the cartView
      if (cartView) {
        saveCart({ ...cartView.cart, products: cartView.products, timekitBooking: cartView.timekitBooking || null });
        setExpressStockCode(cartView.expressStockCode);
        MainTracker.superProperties({ "Purchase Type": type === "only_prescription" ? "Regular" : "Premium" });
      }

      // Update the preselected external pharmacy so in case of upsell from flowzz flow - the shipping layout will change accordingly
      if (externalPharmacyID) {
        setPreSelectedExternalPharmacy(externalPharmacyID);
      }

      // Update the express shipping method to product IDs
      const isExpressSelected = type === "express";
      const tierFee = isExpressSelected && expressTier
        ? latestExpressTiers.current.find(t => t.tier === expressTier)?.fee
        : undefined;
      setExpressDeliveryState((prevState) => ({
        ...prevState,
        isSelected: isExpressSelected,
        selectedTier: isExpressSelected ? (expressTier || null) : null,
        deliveryPrice: tierFee ?? prevState.deliveryPrice,
        shippingMethodToProductIDs: isExpressSelected && cartView?.expressStockCode
          ? { [response.data.shippingMethodID]: getCart().products.filter(p => p.available_stocks?.includes(cartView.expressStockCode!)).map(p => p.product_item_id) }
          : {}
      }))
      
      // Only reactivate coupon if the server still has one active (bonus_coupon_code).
      // Don't use local widget state or URL params here — after an upsell upgrade the server
      // clears bonus_coupon_code, and we must respect that decision.
      const couponCode = cartView.cart.bonus_coupon_code;
      if (couponCode && !cartView.cart.subscription_plan_id && !cartView.cart.auto_bonus) {
        await handleApplyCoupon({inputCode: couponCode, shouldOverrideCartBonuses: true, isExpressSelected});
      } else if (couponWidgetState.inputCode && !cartView.cart.subscription_plan_id && !cartView.cart.auto_bonus) {
        await handleApplyCoupon({inputCode: couponWidgetState.inputCode, shouldOverrideCartBonuses: true, isExpressSelected});
        setIsDeepLinkCouponApplied(true);
      }

      })
      .catch((error) => {
        const cartView = error.response?.data?.cartView || null;
      
        setStickyNotification({
          show: true,
          level: "error",
          showInfoIcon: true,
          title: translate(language, "default", "StickyNotification/generic_server_error_title") as string,
          content: "",
          autoFade: true,
        });
        // Update the cart view
        if (cartView) {
          saveCart({
            ...cartView.cart,
            products: cartView.products
          });
        }
      })
      .finally(() => {
        setIsLoading(false);
      })
  };


  const upgradePrescriptionOnlyToPremium = async (productIdsToRemove: number[], productItemsToAdd: number[]) => {
    try {
      return await http.postToServer("v2/shipping/upgrade-prescription-only-to-premium", {productIdsToRemove, productItemsToAdd}, [http.MIDDLEWARES.CART_ORDER, http.MIDDLEWARES.CUSTOMER_ID])
    } catch (error) {
      window.clientLogger.error('Failed to upgrade prescription only to premium:', error);
    }
  };


  const handleDefaultCartCurrency = (currencies: CountryCurrenciesByID) => {

    //Find the default one: 
    Object.values(currencies).forEach((currency) => {
      if (currency.isDefault) {
        setCartCurrency(currency);
      }
    })

  }




  const handleDigitalCannabisToggle = async (newIsToggleOn: boolean) => {
    setIsSubmitButtonDisabled(true);
    if (newIsToggleOn) {
      // 1. Update the state
      setDigitalCannabisIDState((current) => {
        return { ...current, isToggleLoading: true, }
      })

      // 2. Update The DB
      await incrementProductItemQuantity(digitalCannabisIDState.productItem!, expressDeliveryState.isSelected);

      setDigitalCannabisIDState((current) => {
        return { ...current, isToggleLoading: false, isToggleEnabled: true }
      })


    } else {
      // 1. Update the state
      setDigitalCannabisIDState((current) => {
        return { ...current, isToggleLoading: true }
      })

      // 2. Update The DB
      await decrementProductItemQuantity(digitalCannabisIDState.productItem!, expressDeliveryState.isSelected);

      setDigitalCannabisIDState((current) => {
        return { ...current, isToggleLoading: false, isToggleEnabled: false }
      })
    }

    // 3. Try to reactive coupon 
    const couponCode = cart.bonus_coupon_code || couponWidgetState.inputCode;
    if (couponCode && !cart.subscription_plan_id && !cart.auto_bonus) {
      await handleApplyCoupon({inputCode: couponCode, isExpressSelected: expressDeliveryState.isSelected});
    }
    setIsSubmitButtonDisabled(false);
  }

  const handleOTCProductOutOfStock = async (items: OutOfStockDataState[]) => {

    setIsLoading(true);

    const isOTCOnlyFlow = items.length === cart.products.length && items.every((item) => item.productType === PRODUCT_TYPE_FREE_DRUG && item.newOTCQuantity === 0);


    // Check if the cart has only free drugs and test kits
    const hasFreeDrug = items.some((item) => item.productType === PRODUCT_TYPE_FREE_DRUG);
    const hasTestKit = items.some((item) => item.productType === PRODUCT_TYPE_TEST_KIT);
    const onlyFreeDrugAndTestKit = items.every((item) => item.productType === PRODUCT_TYPE_TEST_KIT || item.productType === PRODUCT_TYPE_FREE_DRUG);
    const isTestKitAndOtc = hasFreeDrug && hasTestKit && onlyFreeDrugAndTestKit ;

    const isTestKitAndOtcOutOfStock = isTestKitAndOtc && items.length === cart.products.length;

    if (isTestKitAndOtcOutOfStock) {
      window.location.href = `${clientConfig.MAIN_WEBSITE_URL}/${uriForOutOfStock}?ec=dnis`;
    }

    // OTC only flow - if all items are out of stock, redirect to the catalog with most selected products
    if (isOTCOnlyFlow) {
      window.location.href = `${clientConfig.MAIN_WEBSITE_URL}/${uriForOutOfStock}?ec=dnis`;
      //clearCart()
    }

    await http.postToServer(`cart/products/update`, {
      productItemsToUpdate: items
    },
      [http.MIDDLEWARES.CART_ORDER, http.MIDDLEWARES.CUSTOMER_ID]
    )
    .then(async (response) => {

      const cartView = response.data.cartView as CartView;

      if (cartView && !isOTCOnlyFlow) {
        // Update the out of stock data state (triggers the out-of-stock popup)
        setOutOfStockDataState({outOfStockData: items, originalProducts: cart.products})

        saveCart({ ...cartView.cart, products: cartView.products, timekitBooking: cartView.timekitBooking || null });
        setExpressStockCode(cartView.expressStockCode);
      }

      // Update the express products
      setExpressDeliveryState((prevState) => ({
        ...prevState,
        deliveryPriceLoading: true,
        shippingMethodToProductIDs: Object.keys(prevState.shippingMethodToProductIDs).reduce((acc, key) => {
          acc[Number(key)] = cartView.products.map((p) => p.product_item_id);
          return acc;
        }, {} as Record<number, number[]>)
      }));

      // try to reactivate coupon
      const couponCode = cartView.cart.bonus_coupon_code || couponWidgetState.inputCode;
      if (couponCode && !cartView.cart.subscription_plan_id && !cartView.cart.auto_bonus) {
        await handleApplyCoupon({inputCode: couponCode, isExpressSelected: expressDeliveryState.isSelected});
      }
      
    })
    .catch((error) => {
      window.clientLogger.error('Failed to update products:', error);
      if (cart.came_from === FLOWZZ_PREMIUM && !isOTCOnlyFlow) {
        setOutOfStockDataState({outOfStockData: items, originalProducts: cart.products})
      }
    })
    .finally(() => {
      setIsLoading(false);
    })
  }

  const handleAddOTCProducts = async (productItems: ProductItem[]) => {
    setIsSubmitButtonDisabled(true);
    try {
      const response = await addProductItemsToCart(productItems)
      const couponCode = cart.bonus_coupon_code || couponWidgetState.inputCode;
      if (response.status === "ok") {
        // 1. Apply coupon if exists
        if (couponCode && !cart.subscription_plan_id && !cart.auto_bonus) {
          await handleApplyCoupon({inputCode: couponCode, isExpressSelected: expressDeliveryState.isSelected});
        }

        // 2. Update the product items in the express delivery state
        if (Object.values(expressDeliveryState.shippingMethodToProductIDs).length > 0) {

          const shippingMethodID = Object.keys(expressDeliveryState.shippingMethodToProductIDs)[0];
          const existingProductIds = Object.values(expressDeliveryState.shippingMethodToProductIDs)[0];

          const newProductIds = productItems.map((item) => Number(item.id));
          const updatedProductIds = [...new Set([...existingProductIds, ...newProductIds])];

          setExpressDeliveryState((prevState) => ({
            ...prevState,
            shippingMethodToProductIDs: {
              [shippingMethodID]: updatedProductIds
            },
          }));
        }
      }
    } catch (error) {
      window.clientLogger.error('Failed to add OTC products', error);
    } finally {
      setIsSubmitButtonDisabled(false);
    }
  }

  const handleOTCProductIncrement = async (productItem: ProductItem, source?: string) => {
    // One test kit per order unless several kits are allowed (per-country switch, overridden by the A/B arm).
    // Safety net for the disabled "+": with the rule off, any kit already in the cart blocks another unit or kit.
    if (productItem.is_test_lab && cart.allow_multiple_test_lab_kits !== true) {
      const testLabInCart = cart.products.some((p) => p.is_test_lab && p.quantity > 0);
      if (testLabInCart) return;
    }

    // The submit lock must come off whatever happens below (a refused update throws), or the page is stuck.
    setIsSubmitButtonDisabled(true);
    try {
      const response = await incrementProductItemQuantity(productItem, expressDeliveryState.isSelected, source);
      const couponCode = cart.bonus_coupon_code || couponWidgetState.inputCode;
      if (response.status === "ok" ){
        // 1. Apply coupon if exists
        if (couponCode && !cart.subscription_plan_id && !cart.auto_bonus) {
          await handleApplyCoupon({inputCode: couponCode, isExpressSelected: expressDeliveryState.isSelected});
        }

        // 2. Update the express delivery state
        if (response.products.length > 0) {
          setExpressDeliveryState((prevState) => ({
            ...prevState,
            shippingMethodToProductIDs: { [Object.keys(prevState.shippingMethodToProductIDs)[0]]: response.products.map((p: CartProductView) => p.product_item_id) },
          }));
        }

        // 3. Update the express stock code
        setExpressStockCode(response.expressStockCode);

        // 4. Update the express delivery state (customer is no longer eligible for express delivery)
        if (expressStockCode && !response.expressStockCode) {
          setExpressDeliveryState((prevState) => ({
            ...prevState,
            deliveryPriceLoading: true,
          }));
        }
      }
    } catch (error) {
      window.clientLogger.error('Failed to increment an OTC product', error);
    } finally {
      setIsSubmitButtonDisabled(false);
    }
  }

  const handleOTCProductDecrement = async (productItem: ProductItem) => {
    setIsSubmitButtonDisabled(true);
    try {
      const response = await decrementProductItemQuantity(productItem, expressDeliveryState.isSelected);
      const couponCode = cart.bonus_coupon_code || couponWidgetState.inputCode;
      if (response.status === "ok" ){
        // 1. Apply coupon if exists
        if (couponCode && !cart.subscription_plan_id && !cart.auto_bonus) {
          await handleApplyCoupon({inputCode: couponCode, isExpressSelected: expressDeliveryState.isSelected});
        }

        // 2. Update the express delivery state
        if (response.products.length > 0) {
          setExpressDeliveryState((prevState) => ({
            ...prevState,
            shippingMethodToProductIDs: { [Object.keys(prevState.shippingMethodToProductIDs)[0]]: response.products.map((p: CartProductView) => p.product_item_id) },
          }));
        }

        // 3. Update the express stock code
        setExpressStockCode(response.expressStockCode);

        // 4. Update the express delivery state (customer is eligible for express delivery)
        if (expressStockCode && !response.expressStockCode) {
          setExpressDeliveryState((prevState) => ({
            ...prevState,
            deliveryPriceLoading: true,
          }));
        }
      }
    } catch (error) {
      window.clientLogger.error('Failed to decrement an OTC product', error);
    } finally {
      setIsSubmitButtonDisabled(false);
    }
  }

  const handleCannabisIDValidation = useCallback(() => {


    // That's because currently german ID has 9 characters so it doesn't accept this number. We will update the code, but in the meantime you can tell customer to create an order with
// ID 111111111 for example and I will update the correct number in db.


    const inputValue = digitalCannabisIDState.personalIDNumber;
    const germanIDRegex = /^[A-Za-z0-9]{9}$/;
    let isInputValid = false
    let errorMsg = "";

    if (!inputValue) {
      errorMsg = "Bitte geben Sie Ihre persönliche Personalausweis-Nummer ein"
      isInputValid = false

    } else if (!germanIDRegex.test(inputValue)) {  //Regex check of german id number
      errorMsg = "Bitte geben Sie eine gültige Personalausweis-Nummer ein"
      isInputValid = false

    } else {
      isInputValid = true
    }

    setDigitalCannabisIDState((prevState) => ({
      ...prevState,
      errorMsg
    }))

    return isInputValid
  }, [digitalCannabisIDState.personalIDNumber])


  const showConnectionIssuesNotification = () => {
    setStickyNotification({
      show: true,
      level: "error",
      showInfoIcon: true,
      autoFade: true,
      title: translate(language, "default", "StickyNotification/danger_title") as string,
      content: translate(language, "default", "StickyNotification/connection_issues") as string,
    })
  }

  const handleAddressValidation = async (formData: DeliveryAddressFormType): Promise<boolean> => {
    const apiKey = clientConfig.BYTEPLANT_API_KEY;
  

    // 1. Skip validation
    if (clientConfig.SKIP_ADRRESS_VALIDATION) {
      return true;
    }
  
    // 2. Skip validation if address is the same as in the customer address (and not express delivery)
    if (
      !expressDeliveryState.isSelected && customerAddress["regular"]?.address === formData.address &&
      customerAddress["regular"]?.zip === formData.zip &&
      customerAddress["regular"]?.city === formData.city
    ) {
      return true;
    }
  
    // 3. Build byteplant api url
    const params = new URLSearchParams({
      StreetAddress: formData.address,
      City: formData.city,
      PostalCode: formData.zip,
      CountryCode: formData.country,
      APIKey: apiKey,
    });
  
    const url = `https://api.address-validator.net/api/verify?${params.toString()}`;
  
    setIsSubmitButtonDisabled(true);
    
    // 4. Call byteplant api
    try {
      const res = await fetch(url);
      const response: ByteplantApiResponse = await res.json();
  
      // Save the response in the state
      setByteplantAddressValidatorState((current) => ({
        ...current,
        responseData: response,
        showModal: ["SUSPECT", "INVALID"].includes(response.status),
      }));
  
      switch (response.status) {
        case "VALID":
          MainTracker.track("custom", "Address Validation Valid", {});
          return true;
  
        case "SUSPECT":
          MainTracker.track("custom", "Address Validation Suspected", {});
          return false;
  
        case "INVALID":
          MainTracker.track("custom", "Address Validation Invalid", {});
          return false;
  
        default:
          MainTracker.track("custom", "Address Validation Not Opened", {});
          return false;
      }
    } catch (error) {
      // fetch rejects with a TypeError when the request never reached the server (e.g. no internet)
      if (error instanceof TypeError) {
        window.clientLogger.warn("Auto Address validation request failed - connection issue", error);
        showConnectionIssuesNotification();
        return false;
      }
      window.clientLogger.error("Auto Address validation request failed", error);
      return false;
    } finally {
      setIsSubmitButtonDisabled(false);
    }
  };
  


  const handleShippingSubmitErrorByCode = async (errorResponse: any) => {

    const errorCode = errorResponse.status;
    const outOfStockData: OutOfStockDataState[] = errorResponse.outOfStockData;

    switch (errorCode) {

      case "sale_voucher_already_used":
        MainTracker.track("custom", "Shipping Post Failure", { "Reason": "sale voucher already used" }); 
        setStickyNotification({
          show: true,
          level: "error",
          showInfoIcon: true,
          autoFade: true,
          title: translate(language, "default", "StickyNotification/sale_voucher_already_used_title") as string,
          content: translate(language, "default", "StickyNotification/sale_voucher_already_used") as string,
        })
        break;

      case "sale_voucher_exhausted":
        MainTracker.track("custom", "Shipping Post Failure", { "Reason": "sale voucher exhausted" }); 

        setStickyNotification({
          show: true,
          level: "error",
          showInfoIcon: true,
          autoFade: true,
          title: translate(language, "default", "StickyNotification/sale_voucher_exhausted_title") as string,
          content: translate(language, "default", "StickyNotification/sale_voucher_exhausted") as string,
        })
        break;

      case "sale_voucher_expired":
        MainTracker.track("custom", "Shipping Post Failure", { "Reason": "sale voucher expired" }); 
        setStickyNotification({
          show: true,
          level: "error",
          showInfoIcon: true,
          autoFade: true,
          title: translate(language, "default", "StickyNotification/sale_voucher_expired_title") as string,
          content: translate(language, "default", "StickyNotification/sale_voucher_expired") as string,
        })
        break;

      case "sale_voucher_missing":
        MainTracker.track("custom", "Shipping Post Failure", { "Reason": "sale voucher missing" }); 
        setStickyNotification({
          show: true,
          level: "error",
          showInfoIcon: true,
          autoFade: true,
          title: translate(language, "default", "StickyNotification/sale_voucher_missing_title") as string,
          content: translate(language, "default", "StickyNotification/sale_voucher_missing") as string,
        })
        break;

      case "invalid_sale_voucher":
        MainTracker.track("custom", "Shipping Post Failure", { "Reason": "invalid sale voucher" }); 
        setStickyNotification({
          show: true,
          level: "error",
          showInfoIcon: true,
          autoFade: true,
          title: translate(language, "default", "StickyNotification/invalid_sale_voucher_title") as string,
          content: translate(language, "default", "StickyNotification/invalid_sale_voucher") as string,
        })
        break;

      case "already_subscribed":
        MainTracker.track("custom", "Shipping Post Failure", { "Reason": "already subscribed" }); 
        navigate({ pathname: `/${country}/account/orders-subscription`, search: `?ec=as` }); // as = already_subscribed
        break;

      case "dosage_not_in_stock":
      case "product_not_in_stock":
        MainTracker.track("custom", "Shipping Post Failure", { "Reason": "dosage not in stock", "Out Of Stock Product Item IDs": outOfStockData?.map((p) => p.outOfStockProductItemId)  });
        if(outOfStockData){
          await handleOTCProductOutOfStock(outOfStockData)

          // Non-Flowzz always applies this; Flowzz Premium only applies it for the Control
          // cohort - Treatment stays out of this block entirely so the modal (and, for an
          // all-cannabis-OOS cart, the alternative-offer flow) can show instead.
          if (cart.came_from !== FLOWZZ_PREMIUM || !isAlternativeOfferTreatment) {
            const prescriptionDrugProductItemIDs = outOfStockData.filter((p) => p.productType === PRODUCT_TYPE_PRESCRIPTION_DRUG && p.newProductItemId === 0);
            const cartPrescriptionDrugProductItemIDs = cart.products.filter((product) => product.type === PRODUCT_TYPE_PRESCRIPTION_DRUG);
            if (prescriptionDrugProductItemIDs.length !== 0 && prescriptionDrugProductItemIDs.length === cartPrescriptionDrugProductItemIDs.length) {
              navigate({
                pathname: `/select_treatment/${country}/${language}/${categoryCatalogMention}`,
                search: `?${searchParams.toString()}${!searchParams.has('ec') ? '&ec=dnis' : ''}`
              });
            }

            const testKitProductItemIDs = outOfStockData.filter((p) => p.productType ===  PRODUCT_TYPE_TEST_KIT && p.newProductItemId === 0);
            const cartTestKitProductItemIDs = cart.products.filter((product) => product.type === PRODUCT_TYPE_TEST_KIT);
            const allProductsAreTestKits = cart.products.every((product) => product.type === PRODUCT_TYPE_TEST_KIT);

            if (allProductsAreTestKits && testKitProductItemIDs.length !== 0 && testKitProductItemIDs.length === cartTestKitProductItemIDs.length) {
              navigate({
                pathname: `/select_treatment/${country}/${language}/${categoryCatalogMention}`,
                search: `?${searchParams.toString()}${!searchParams.has('ec') ? '&ec=dnis' : ''}`
              });
            }
          }
        }
        else {
          navigate({
            pathname: `/select_treatment/${country}/${language}/${categoryCatalogMention}`,
            search: `?${searchParams.toString()}${!searchParams.has('ec') ? '&ec=dnis' : ''}`
          });
        }

        break;

      case "missing_cart_products":
        if (cart.came_from === FLOWZZ_PREMIUM) {
          MainTracker.track("custom", "Shipping Post Failure", { "Reason": "missing cart products" });
          const cartView = errorResponse.cartView as CartView;
          const productsToMap = outOfStockDataState.originalProducts.length > 0 ? outOfStockDataState.originalProducts : cart.products;
          const syntheticOutOfStock = productsToMap.map(product => ({
            outOfStockProductItemId: product.product_item_id,
            productType: product.type || PRODUCT_TYPE_FREE_DRUG,
            newProductItemId: 0,
            newOTCQuantity: 0,
          }));
          setOutOfStockDataState({
            outOfStockData: syntheticOutOfStock,
            originalProducts: productsToMap
          });
          if (cartView) {
            saveCart({ ...cartView.cart, products: cartView.products, timekitBooking: cartView.timekitBooking || null });
            setExpressStockCode(cartView.expressStockCode);
          }
        }
        break;

      case "missing_stock_products_in_cart":
        MainTracker.track("custom", "Shipping Post Failure", { "Reason": "missing stock products in cart" }); 
        navigate({
          pathname: `/select_treatment/${country}/${language}/${categoryCatalogMention}`,
          search: `?${searchParams.toString()}${!searchParams.has('ec') ? '&ec=dnis' : ''}`
        });

        break;

      case "dosage_not_in_stock_for_subscription":
      MainTracker.track("custom", "Shipping Post Failure", { "Reason": "dosage not in stock for subscription" }); 
      navigate({ pathname: `/select_treatment/${country}/${language}/${categoryCatalogMention}`, search: `?ec=dnis` }); // dnis = dosage_not_in_stock
      break;
      
      case "otc_product_not_in_stock":
        MainTracker.track("custom", "Shipping Post Failure", { "Reason": "OTC Product not in stock" }); 
        if(outOfStockData){
          await handleOTCProductOutOfStock(outOfStockData)
        }
        break;

      case "no_stock_suitable_for_subscription":
        MainTracker.track("custom", "Shipping Post Failure", { "Reason": "no stock suitable for subscription" }); 
        deactivateSubscriptionInDB();
        setStickyNotification({
          show: true,
          level: "error",
          showInfoIcon: true,
          autoFade: true,
          title: translate(language, "default", "StickyNotification/danger_title") as string,
          content: translate(language, "default", "StickyNotification/product_not_in_stock_for_subscription") as string,
        })
        break;

      case "country_not_allowed":
        MainTracker.track("custom", "Shipping Post Failure", { "Reason": "country does not equal destinationCountry" }); 
        setStickyNotification({
          show: true,
          level: "error",
          showInfoIcon: true,
          autoFade: true,
          title: translate(language, "default", "StickyNotification/danger_title") as string,
          content: translate(language, "default", "StickyNotification/country_does_not_equal_destinationCountry") as string,
        })
        break;


      case "invalid_order_price":
      case "orders_total_price_0":
        MainTracker.track("custom", "Shipping Post Failure", { "Reason": "Order price cannot be 0 or less" });
        setStickyNotification({
          show: true,
          level: "error",
          showInfoIcon: true,
          autoFade: true,
          title: translate(language, "default", "StickyNotification/danger_title") as string,
          content: translate(language, "default", "StickyNotification/invalid_order_price") as string,
        })
        break;

      case "missing_product_for_uber":
      case "missing_product_for_express":
        setExpressDeliveryState((prevState) => ({
          ...prevState,
          shippingMethodToProductIDs: errorResponse.shippingMethodToProductIDs,
          showSplitDeliveryPopup: true,
          isExpressOnly: errorResponse.hasExpressOnlyProducts || errorResponse.hasUberOnlyProducts,
        }));
        break;


      case "phone_number_invalid":
        setStickyNotification({
          show: true,
          level: "warning",
          showInfoIcon: true,
          autoFade: true,
          title: translate(language, "default", "StickyNotification/express_phone_number_invalid") as string,
          content: translate(language, "default", "StickyNotification/express_phone_number_invalid_description") as string,
        });
        break;

      case "timekit_resource_already_reserved":
        setShowRescheduleVideoConsultationModal(true);
        break;
        
      case "no_stock_for_selected_shipping_method":
      case "no_pharmacies_in_range_for_uber":
      case "no_pharmacies_in_range_for_express":
      case "express_no_coverage":
      case "shipping_company_not_found":
      case "stock_shipping_company_not_found":
        setStickyNotification({
          show: true,
          level: "warning",
          showInfoIcon: true,
          autoFade: true,
          title: translate(language, "default", "StickyNotification/express_delivery_unavailable_title") as string,
          content: translate(language, "default", "StickyNotification/express_delivery_unavailable_description") as string,
        });
        await handleDeliveryOptionChange("regular");
        break;


      default:
        MainTracker.track("custom", "Shipping Post Failure", { "Reason": errorCode || "unknown error" }); 
        setStickyNotification({
          show: true,
          level: "error",
          showInfoIcon: true,
          autoFade: true,
          title: translate(language, "default", "StickyNotification/danger_title") as string,
          content: "Server Failed",
        })
        await handleDeliveryOptionChange("regular");
        break;
    }
  }

  const handleShippingSubmit = async (formData: DeliveryAddressFormType | DHLPackstationFormType, customerUsedAddressSuggestion?: boolean, isExpress?:boolean, skipUpsell?: boolean, wasUpgradedToPremiumParam?: boolean) => {
    
    // 1. Validate CannabisId input if added to order:
    if (digitalCannabisIDState.isToggleEnabled) {
      const isCannabisIDValid = handleCannabisIDValidation()
      if (!isCannabisIDValid) {
        //Scroll to digital-cannabis-id-widget-container
        if (digitalCannabisIDState.widgetRef) {
          digitalCannabisIDState.widgetRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
        }
        return
      }
    }


    // 2. Validate IdentityCheck
    if (identityCheckState.showWidget && !identityCheckState.isPrecautionToggleEnabled) {
      setIdentityCheckState((prevState) => ({
        ...prevState,
        precautionLevel: "error"
      }))

      return
    }

    // 2b. RX Self-Pickup: block Continue if no pharmacy is selected
    if (getCart().selected_service_option === SELF_PICKUP && rxSelfPickup.isEligible && !selectedPickupPharmacyCode) {
      setPharmacyRequiredError(true);
      return;
    }

    const voucherNeedsCheck = (saleVoucherRequired || saleVoucherCode.trim() !== '') && !isVoucherValid;

    // 3. Validate address, if we didn't do it already (no responseData by byteplant)
    const byteplantSuggestion = byteplantAddressValidatorState.responseData;

    let isAddressAutoVerified = false;
    if (!voucherNeedsCheck) {
      if (customerUsedAddressSuggestion || byteplantAddressValidatorState.customerUsedSuggestion) {
        isAddressAutoVerified = true;
      } else if ((!byteplantSuggestion && selectedFormID === "delivery-address-form") || expressDeliveryState.isSelected) {
        isAddressAutoVerified = await handleAddressValidation(formData as DeliveryAddressFormType);
        if (!isAddressAutoVerified) { //Will open the Validator modal
          return
        }
      } else {
        isAddressAutoVerified = false;
      }
    }


    // 3b. Re-validate express in range when the submitted zip diverges from the quoted (cart) zip.
    // The byteplant suggestion path rewrites the form zip via setValue without going through
    // updateCartZipcode, so an express (e.g. next_day) order could otherwise be placed on a zip its
    // tier was never quoted for (HL-9096, Bug 2). getExpressQuote reverts to DHL + notifies when the
    // new zip is out of range, in which case we stop here and let the customer re-submit as regular.
    if (shouldRevalidateExpressZip((formData as DeliveryAddressFormType).zip, getCart().zipcode, expressDeliveryState.isSelected)) {
      const stillInRange = await getExpressQuote(formData as DeliveryAddressFormType);
      if (!stillInRange) {
        return;
      }
    }

    // 4. Check if all cart products are prescription-only and handle upsell (skip if skipUpsell is true or popup already dismissed)
    if (!skipUpsell && !upsellPopupDismissed) {
      const cart = getCart();
      const prescriptionDrugProducts = cart.products.filter((product: any) => product.type === "prescription_drug");
      const allProductsArePrescriptionOnly = prescriptionDrugProducts.every((product: any) => product.only_prescription);
      if (allProductsArePrescriptionOnly && cart.flow === ORDER_FLOWS.MULTIPLE_PRODUCTS && contentType === "cannabis") {
        try {
          // Call the API to handle upsell popup logic
          const response = await http.postToServer(`v2/shipping/get-upsell-products`, {
            cartProducts: cart.products,
            flow: cart.flow,
            countryID: country
          }, [http.MIDDLEWARES.CART_ORDER, http.MIDDLEWARES.CUSTOMER_ID]);
          
          const upsellResult = response.data;
          if (upsellResult?.showUpsellPopUp ) {
            // Set upsell state to show the popup using existing state variables
            setShowUpsellPopUp(true);
            setEligibleForUpsell(true);
            setIsFlowzzMoreExpensive(upsellResult.isFlowzzMoreExpensive || false);
            setUpsellSaveAmount(upsellResult.upsellSaveAmount || 0);
            setUpsellProducts(upsellResult.cartProducts || []);
            setFinalAlternativeProducts(upsellResult.finalAlternativeProducts || {});
            setCurrentFormData(formData);

            return; // Don't proceed to payment, stay on shipping page
          } else if (upsellResult.showUpsellPopUp === false) {
            window.eventsTracking.track({ e: "Upsell Popup does not appear", p: {notShowUpsellPopUpReason: upsellResult.notShowUpsellPopUpReason } });
          }
        } catch (error: any) {
          const errorMessage = error?.response?.data?.message;
          const errorCode = error?.response?.data?.code;
          if (errorCode === "dosage_not_in_stock") {
            window.clientLogger.warn("Error handling upsell popup:", errorMessage);
          }
          else {
            window.clientLogger.error("Error handling upsell popup:", errorMessage);
          }
        }
      }
    }

    // 4b. RX Premium Upsell — trigger when the user chose prescription-only on an Rx (non-cannabis) order
    // in a supported market. Different from the cannabis upsell: this checks cart.selected_service_option
    // (the delivery option the user actively selected), not product.only_prescription (a product capability flag).
    const freshCart = getCart();
    const isRxNonCannabis =
      contentType !== 'cannabis' &&
      freshCart.products.some((p: any) => p.type === 'prescription_drug');

    // HL-11357: this popup offers to upgrade the prescription-only order into a delivered one —
    // exactly what settings.disallow_one_time_premium forbids. With no plan on the cart that
    // upgrade cannot be ordered (OrdersController rejects it as one_time_premium_not_allowed) and
    // accepting it clears only_prescription, so the customer loses the choice they made on Choose
    // Plan and lands on a rejection. The server already withholds the cannabis upsell for these
    // carts (getUpSellInfoIfNeeded); this one is decided here, so it has to ask the same question.
    // Strict false: a cart from before the field existed keeps the old behaviour.
    const deliveryIsOrderable = freshCart.allow_one_time_premium !== false;

    if (
      !skipUpsell &&
      !rxUpsellDismissed &&
      freshCart.selected_service_option === 'only_prescription' &&
      deliveryIsOrderable &&
      isRxNonCannabis &&
      RX_UPSELL_MARKETS.includes(country || '')
    ) {
      setByteplantAddressValidatorState(prev => ({ ...prev, showModal: false }));
      setShowRxPremiumUpsellPopup(true);
      setRxUpsellDismissed(true);
      setCurrentFormData(formData);
      return;
    }

    const showFastReviewUpsell = (cart.selected_service_option === "self_pickup" || cart.selected_service_option === "only_prescription") && !cart.is_express_prescription_review && !isExpressPrescriptionReview;
    const upsellAlreadyShown = isFastReviewUpsellShown.current;
    const isFlowzzFlow = cart.flow === ORDER_FLOWS.FLOWZZ_PRE_SELECTED;
    const isCannaleoFlow = cart.flow === ORDER_FLOWS.CANNALEO_PRE_SELECTED;
    const isCannabisOrder = contentType === "cannabis";
    
    if (!voucherNeedsCheck && isFastApprovalFlow && showFastReviewUpsell && !upsellAlreadyShown && !isFlowzzFlow && !isCannaleoFlow && isCannabisOrder) {
      // Track psale_offer_shown (once per order)
      if (!upsaleOfferShownTracked.current) {
        const customerID = customerState?.customerID || null;
        const cartItemsCount = cart.products?.length || 0;
        const pickupPharmaciesCount = productsGroupedByPharmacy?.length || 0;
        MainTracker.track('custom', 'upsale_offer_shown', {
          user_id: customerID,
          cart_id: cart.id,
          method: 'pickup',
          approval: 'standard',
          contents: 'rx_plus_meds',
          cart_items_count: cartItemsCount,
          pickup_pharmacies_count: pickupPharmaciesCount,
        });
        upsaleOfferShownTracked.current = true;
      }
      setCurrentFormData(formData);
      setShowFastReviewUpsell(true);
      return;
    }

    // 4b. Express Delivery Upsell — only for real courier delivery to a street address.
    // Skip: self-pickup / only-prescription (nothing physical ships now, so nothing to expedite),
    // DHL Packstation (couriers can't deliver to a parcel locker), and express-prescription-review
    // (already a premium flow, handled by the fast-review upsell above).
    const isPackstationForm = selectedFormID === "dhl-packstation-form";
    if (
      isExpressFlow &&
      !expressDeliveryUpsellShown.current &&
      cart.selected_service_option !== "express" &&
      cart.selected_service_option !== SELF_PICKUP &&
      cart.selected_service_option !== "only_prescription" &&
      !cart.is_express_prescription_review &&
      !isExpressPrescriptionReview &&
      !isPackstationForm &&
      (expressDeliveryState.isAvailable || availableExpressTiers.length > 0)
    ) {
      let tiersToCheck = availableExpressTiers;

      // If tiers not fetched yet, trigger quote inline
      if (tiersToCheck.length === 0 && (formData as DeliveryAddressFormType).zip) {
        const quoteFormData = formData as DeliveryAddressFormType;
        const inRange = await getExpressQuote({ address: quoteFormData.address, zip: quoteFormData.zip, city: quoteFormData.city, country: quoteFormData.country, phone: quoteFormData.phone });
        if (inRange) {
          // getExpressQuote sets availableExpressTiers via setState, but we can't read it yet.
          // Re-fetch from the ref or re-derive from the API response.
          // Use a ref to capture tiers synchronously.
          tiersToCheck = latestExpressTiers.current;
        }
      }


      // Coverage guard: address-in-range only proves express *reaches* the customer. The
      // quoted express stock must also be able to ship the cart's products, or the order POST
      // throws missing_product_for_express server-side (HL-9096). latestExpressCoverage is
      // populated by the quote above (inline or initial), so it's safe to read here.

      const cartProductItemIDs = cart.products.filter(p => p.type !== CANNABIS_ID).map(p => p.product_item_id);
      const expressCoveredIDs = latestExpressCoverage.current;
      const allCovered = doesExpressStockCoverCart(cartProductItemIDs, expressCoveredIDs);

      if (tiersToCheck.length > 0 && allCovered) {
        const variant = resolveExpressUpsellVariant(tiersToCheck);
        if (variant) {
          setExpressUpsellVariant(variant);
          setCurrentFormData(formData);
          setShowExpressDeliveryUpsell(true);
          expressDeliveryUpsellShown.current = true;
          MainTracker.track('custom', SHIPPING_EVENTS.UPSELL_POPUP, buildUpsellPopupProps({ cart, variant }));
          return;
        }
      }
    }

    // 5. MP Events
    MainTracker.superProperties({ "Cannabis ID": digitalCannabisIDState.isToggleEnabled ? "Yes" : "No" });
    if (expressDeliveryState.isAvailable && expressDeliveryState.isEnabled) {
      window.eventsTracking.track({e: "Uber eligible order"});
    }

    setIsLoading(true);

    // Past every early-return gate — committed to creating the order. Block all deferred
    // cart-mutating ops from here on; the server soft-deletes the cart on order creation (HL-9096).
    isOrderSubmittingRef.current = true;

    // 6. Post To the server
    let deviceProperties = null;
    let userProperties = null;
    try {
      deviceProperties = {
        inner_width: window.innerWidth,
        inner_height: window.innerHeight,
        screen_width: window.screen.width,
        screen_height: window.screen.height,
        pixel_ratio: window.devicePixelRatio,
        os: (navigator as any).userAgentData?.platform || navigator.platform || "",
        machine: (navigator as any).userAgentData?.architecture || "",
        connection_type: (navigator as any).connection?.effectiveType ?? null,
        device_type: /Android/.test(navigator.userAgent) ? "android" : /iPhone/.test(navigator.userAgent) ? "iphone" : /iPad/.test(navigator.userAgent) ? "ipad" : "desktop",
      };
      userProperties = {
        language: navigator.language,
        is_dark_mode: window.matchMedia?.("(prefers-color-scheme: dark)")?.matches ?? false,
        web_language: document.documentElement.lang || "",
        device_memory: (navigator as any).deviceMemory ?? null,
      };
    } catch (e) {}

    const getShippingMethodToProductIDs = () => {
      if (isExpress) return expressDeliveryState.shippingMethodToProductIDs;
      if (getCart().selected_service_option === 'self_pickup' && selfPickupShippingMethodID) return { [selfPickupShippingMethodID]: [] };
      if (standardShippingMethodID) return { [standardShippingMethodID]: [] };
      return undefined;
    };

    http.postToServer(`v2/orders`, {
      shippingFormData: formData,
      formType: selectedFormID === "delivery-address-form" ? "delivery" : "dhl_packstation",
      isAddressAutoVerified,
      personalIDNumber: digitalCannabisIDState.personalIDNumber.toUpperCase(),
      trackingData: window.eventsTracking && window.eventsTracking.getData(),
      shippingMethodToProductIDs: getShippingMethodToProductIDs(),
      expressStockCode: isExpress ? expressStockCode : undefined,
      isExpressPrescriptionReview,
      saleVoucherCode: saleVoucherCode.trim(),
      wasUpgradedToPremium: wasUpgradedToPremiumParam ?? wasUpgradedToPremium,
      restoredCart: searchParams.get("rc") === "true" ? true : false,
      restoredByCartID: window.eventsTracking && window.eventsTracking.getData('restored_by_cart_id'),
      deviceProperties,
      userProperties,
      service_fee_test_variant: localStorage.getItem(`abtest_${SERVICE_FEE_TEST_NAME}`) || undefined,
      country,
    },
      [http.MIDDLEWARES.CART_ORDER, http.MIDDLEWARES.CUSTOMER_ID]
    )
    .then(response => {
        const responseData = response.data
        const newOrderHashID = responseData.newOrderHashID;
        const thankyouHash = responseData.thankyouHash;
        const shouldSkipPaymentPage = isZeroOrderPrice(Number(responseData.totalOrdersPrice));
        const postShippingSearchParams = new URLSearchParams(searchParams.toString());
        if (thankyouHash) {
          postShippingSearchParams.set("thankyouHash", thankyouHash);
        }

      try {
        sessionStorage.removeItem('cpn');
        // HL-10959: defensive merge so Placed Order always carries arm + entry_point
        registerWlExperimentSuperProperties();
        MainTracker.track("custom", "Placed Order", {
          ...responseData,
          'Shipping Fee': isExpress ? cart.custom_shipping_fee : cart.shipping_fee,
          'Coupon': cart.bonus_value,
          'Service Fee': cart.service_fee,
          'Medication without Service Fee': cart.drugs_price?.toFixed(2),
          ...getWlExperimentTrackingProps(),
        });
        window.eventsTracking.setData("last_order", responseData.newOrderHashID);
        window.eventsTracking.setData("last_order_country", country);
        if (country) (window as any).eventsStorage.addToCookie('last_order_country', country.toUpperCase());
        window.eventsTracking.setData("last_order_action", Date.now());
        SessionStorageWrapper.setItem("last_shipped_cart", cart.id);

        // save_questionnaire_answers A/B test: the order is now created on
        // the server. Drop the locally saved questionnaire answers for this
        // questionnaire (the marker was dropped earlier by Questionnaire.tsx
        // / SkipQuestionnaireModal on a successful submit).
        const pendingClear = consumePendingClear();
        if (pendingClear) {
          clearQuestionnaireAnswers(pendingClear.countryID, pendingClear.categoryID);
        }

        // TODO: Fire tag manager events: 


        clearCart();
        // Fire Product Cart View here — after all upsells, delivery-option changes, and
        // order creation — so the DOM price and the actual charged price are in sync.
        // The Customer ID matches paymentEventSent, linking this event to Final Cart Price.
        MainTracker.track("products", "Product Cart View", {
          "Cart Price": document.getElementById("cart_final_price")?.textContent,
          "Customer ID": sessionStorage.getItem('paymentEventSent'),
        });
      } catch(error) {
        window.clientLogger.error("Error sumbit shipping, still navigating to payment", {code: 'shipping_error_still_navigating_to_payment'},error);
      }
      const postShippingPath = shouldSkipPaymentPage
        ? `/thank-you/${country}/${language}/${newOrderHashID}?${postShippingSearchParams.toString()}`
        : `/payment/${country}/${language}/${newOrderHashID}?${searchParams.toString()}`;
      markPaymentFromShipping(newOrderHashID);
      navigate(postShippingPath, { state: { from: "shipping" } })

    })
    .catch(async (error) => {
      // Order failed — the cart was NOT soft-deleted, so the customer stays on the page and may
      // retry / re-edit. Re-enable the cart-mutating ops we blocked before the POST (HL-9096).
      isOrderSubmittingRef.current = false;

      // No response means the request never reached the server (no internet / timeout)
      if (!error.response) {
        MainTracker.track("custom", "Shipping Post Failure", { "Reason": "connection_issues" });
        showConnectionIssuesNotification();
        window.clientLogger.warn("Error sumbit shipping - connection issue", error);
        return;
      }

      const errorResponse = error.response?.data;
      await handleShippingSubmitErrorByCode(errorResponse);

      if (errorResponse?.logLevel === "warn") {
        window.clientLogger.warn("Error sumbit shipping", error);
      }
      else {
        window.clientLogger.error("Error sumbit shipping", error);
      }

    })
    .finally(() => {
      setIsLoading(false);
    })
  }

  const handleExpressUpsellApply = async () => {
    await handleDeliveryOptionChange("express", expressUpsellVariant || undefined);
    setShowExpressDeliveryUpsell(false);
    MainTracker.track('custom', SHIPPING_EVENTS.UPSELL_APPLY, {});

    MainTracker.track('custom', 'express_delivery_upsell_accepted', {
      variant: expressUpsellVariant,
      user_id: customerState?.customerID || null,
      cart_id: cart.id,
    });

    MainTracker.track('custom', 'upsale_accepted', {
      user_id: customerState?.customerID || null,
      order_id: cart.id || null,
      upsell_type: 'express',
      variant: expressUpsellVariant,
      fee_cents: expressUpsellVariant ? UPSELL_CONFIGS[expressUpsellVariant].priceCents : null,
    });
};

  const handleExpressUpsellContinue = () => {
    setShowExpressDeliveryUpsell(false);
    MainTracker.track('custom', 'express_delivery_upsell_declined', {
      variant: expressUpsellVariant,
      user_id: customerState?.customerID || null,
      cart_id: cart.id,
      decline_action: 'continue_dhl',
    });
    if (currentFormData) {
      handleShippingSubmit(currentFormData);
    }
  };

  const handleRxUpsellAccept = async () => {
    setShowRxPremiumUpsellPopup(false);
    setShowRxRegretBanner(false);
    setRxWasUpgradedToPremium(true);
    localStorage.removeItem(RX_UPSELL_DECLINED_KEY);
    await handleDeliveryOptionChange('regular', undefined, { isRxUpsell: true });
    if (currentFormData) {
      handleShippingSubmit(currentFormData, false, false, true, true);
    }
  };

  const handleRxUpsellDecline = () => {
    setShowRxPremiumUpsellPopup(false);
    if (currentFormData) {
      handleShippingSubmit(currentFormData, false, false, true);
    }
  };

  const handleRxUpsellClose = () => {
    setShowRxPremiumUpsellPopup(false);
    localStorage.setItem(RX_UPSELL_DECLINED_KEY, 'true');
    setShowRxRegretBanner(true);
  };

  const handleRxRegretDismiss = () => {
    setShowRxRegretBanner(false);
    if (currentFormData) {
      handleShippingSubmit(currentFormData, false, false, true);
    }
  };

  const handleExpressUpsellClose = () => {
    setShowExpressDeliveryUpsell(false);
    MainTracker.track('custom', 'express_delivery_upsell_declined', {
      variant: expressUpsellVariant,
      user_id: customerState?.customerID || null,
      cart_id: cart.id,
      decline_action: 'close_modal',
    });
    if (currentFormData) {
      handleShippingSubmit(currentFormData);
    }
  };

  return (
    <ShippingPageContext.Provider
      value={{
        language,
        country,
        finishedFetching,
        setFinishedFetching,
        selectedOptionPanelCode,
        setSelectedOptionPanelCode,
        isGoogleAPILoaded,
        setIsGoogleAPILoaded,
        hasUserChosenDeliveryOption,
        wasExpressPreSelected,
        selectedFormID,
        setSelectedFormID,
        saleVoucherCode,
        setSaleVoucherCode,
        saleVoucherRequired,
        setSaleVoucherRequired,
        isVoucherValid,
        setIsVoucherValid,
        handleDeliveryOptionChange,
        countryCurrenciesByID,
        setCountryCurrenciesByID,
        customerAddress,
        setCustomerAddress,
        allCustomerAddresses,
        setAllCustomerAddresses,
        invalidPhoneAddress,
        setInvalidPhoneAddress,
        preSelectedExternalPharmacy,
        setPreSelectedExternalPharmacy,
        selfPickupPharmacySettings,
        setSelfPickupPharmacySettings,
        hidePackstation,
        setHidePackstation,
        contentType,
        setContentType,
        testLabSampleType,
        setTestLabSampleType,
        isMixedOtcTestLabCart,
        setIsMixedOtcTestLabCart,
        isPackstationFirst,
        isAlternativeOfferTreatment,
        abtestAutoCatalogCoupon,
        setAbtestAutoCatalogCoupon,
        urbifyRolloutPercentage,
        setUrbifyRolloutPercentage,
        isUrbifyFullyRolledOut,
        isReturningCustomer,
        setIsReturningCustomer,
        rxSelfPickup,
        setRxSelfPickup,
        selectedPickupPharmacyCode,
        setSelectedPickupPharmacyCode,
        rxSelfPickupServiceOption,
        setRxSelfPickupServiceOption,
        isPickupSheetOpen,
        setIsPickupSheetOpen,
        pharmacyRequiredError,
        setPharmacyRequiredError,
        isRxPickupFastApproval,
        setIsRxPickupFastApproval,
        categoryCatalogMention,
        setCategoryCatalogMention,
        allowPrescriptionRedemption,
        setAllowPrescriptionRedemption,
        isSubscriptionAlreadyPicked,
        setIsSubscriptionAlreadyPicked,
        OTCAddonsProducts,
        setOTCAddonsProducts,
        showOTCAddonsModal,
        setShowOTCAddonsModal,
        showOtcCartPopUp,
        setShowOtcCartPopUp,
        couponWidgetState,
        setCouponWidgetState,
        handleApplyCoupon,
        isDeepLinkCouponApplied,
        setIsDeepLinkCouponApplied,
        digitalCannabisIDState,
        setDigitalCannabisIDState,
        identityCheckState,
        byteplantAddressValidatorState,
        setByteplantAddressValidatorState,
        setIdentityCheckState,
        isSubmitButtonDisabled,
        setIsSubmitButtonDisabled,
        setForceFreeTreatmentFeeCoupon,
        forceFreeTreatmentFeeCoupon,
        getExpressQuote,
        expressDeliveryState,
        setExpressDeliveryState,
        showUpsellPopUp,
        setShowUpsellPopUp,
        isFlowzzMoreExpensive,
        setIsFlowzzMoreExpensive,
        upsellSaveAmount,
        setUpsellSaveAmount,
        upsellProducts,
        setUpsellProducts,
        finalAlternativeProducts,
        setFinalAlternativeProducts,
        currentFormData,
        setCurrentFormData,
        showUpsellOnShippingPage,
        setshowUpsellOnShippingPage,
        eligibleForUpsell,
        setEligibleForUpsell,
        upgradePrescriptionOnlyToPremium,
        wasUpgradedToPremium,
        setWasUpgradedToPremium,
        upsellABCtestWeights,
        setUpsellABCtestWeights,
        showRescheduleVideoConsultationModal,
        setShowRescheduleVideoConsultationModal,
        isOriginalCartFlowFlowzz,
        setIsOriginalCartFlowFlowzz,
        setUriForOutOfStock,
        outOfStockDataState,
        setOutOfStockDataState,
        setExpressStockCode,
        updateCartZipcode,
        expressStockCode,
        otcOnlyShippingFee,
        setOtcOnlyShippingFee,
        setUpsellPopupDismissed,
        upsellPopupDismissed,
        showRxPremiumUpsellPopup,
        setShowRxPremiumUpsellPopup,
        showRxRegretBanner,
        rxWasUpgradedToPremium,
        handleRxUpsellAccept,
        handleRxUpsellDecline,
        handleRxUpsellClose,
        handleRxRegretDismiss,
        //Handlers
        handleDigitalCannabisToggle,
        handleAddOTCProducts,
        handleOTCProductIncrement,
        handleOTCProductDecrement,
        handleShippingSubmit,
        handleAddressValidation,
        handleDefaultCartCurrency,
        pharmaciesModalInfo,
        setPharmaciesModalInfo,
        showFastReviewUpsell,
        setShowFastReviewUpsell,
        isFastReviewUpsellShown,
        allPharmaciesLocations,
        setAllPharmaciesLocations,
        isExpressPrescriptionReview,
        setIsExpressPrescriptionReview,
        productsGroupedByPharmacy,
        setProductsGroupedByPharmacy,
        isCannabisCatalog,
        setIsCannabisCatalog,
        isExpressFlow,
        setIsExpressFlow,
        isCarePlusOrder,
        setIsCarePlusOrder,
        isFastApprovalFlow,
        setIsFastApprovalFlow,
        showExpressDeliveryUpsell,
        setShowExpressDeliveryUpsell,
        expressUpsellVariant,
        expressDeliveryUpsellShown,
        availableExpressTiers,
        setAvailableExpressTiers,
        handleExpressUpsellApply,
        handleExpressUpsellContinue,
        handleExpressUpsellClose,
        standardShippingMethodID,
        setStandardShippingMethodID,
        selfPickupShippingMethodID,
        setSelfPickupShippingMethodID,
      }}
    >
      {children}
    </ShippingPageContext.Provider>
  );
};

// Custom hook to use the context
export const useShippingPageContext = (): ShippingPageContextType => {
  const context = useContext(ShippingPageContext);
  // if (!context) {
  //   throw new Error("useShippingPageContext must be used within a ShippingPageProvider");
  // }
  return context || {} as ShippingPageContextType;
};
